import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { api } from "../api/apiClient";
import type {
  BouquetBuildSummary,
  CatalogItem,
  CreateSaleLinePayload,
  CreateSalePayload,
  CustomerOrder,
  StaffOrderPaymentMethod,
  RingUpOrderPayload,
  Sale,
} from "../api/types";
import { ORDER_PAYMENT_METHODS } from "../components/orders/RecordPaymentModal";
import { BuildBouquetForSaleModal } from "../components/sale/BuildBouquetForSaleModal";
import {
  OfficePaymentPicker,
  officePaymentProblemText,
} from "../components/sale/OfficePaymentPicker";
import {
  OFFICE_PAYMENT_DEFAULT,
  OFFICE_PAYMENT_NONE,
  lineCents,
  readOfficePaymentChoice,
  type OfficePaymentChoice,
} from "../utils/paymentSplit";
import { useBoxPickPrompt } from "../components/shop-floor/useBoxPickPrompt";
import {
  NO_FIGURE,
  afterCostLabel,
  bouquetCostText,
  hasUnknown,
  knownAmount,
  partlyKnownCost,
} from "../utils/unknownCost";
import { useAuth } from "../state/AuthContext";
import type { DashboardOutletContext } from "./dashboardOutletContext";

export type SaleNewPageProps = {
  /** When true, stay on the page after recording (home embed); no redirect to sales list. */
  embedOnHome?: boolean;
};

type LineDraft =
  | {
      uid: string;
      kind: "BOUQUET";
      bouquetBuildId: string;
      unitPriceEur: string;
    }
  | {
      uid: string;
      kind: "ITEM";
      itemId: string;
      quantity: string;
      unitPriceEur: string;
    };

const fmt = (v: number) => `€${v.toFixed(2)}`;
const newUid = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export function SaleNewPage({ embedOnHome = false }: SaleNewPageProps) {
  const { user } = useAuth();
  const { locations } = useOutletContext<DashboardOutletContext>();
  const navigate = useNavigate();
  // G-3: arriving via "Ring up" on an order links the sale to it on creation.
  const [searchParams] = useSearchParams();
  const linkOrderId = searchParams.get("orderId");
  const isOwner = user?.role === "OWNER";
  // KI-152: a bouquet's cost reaches owners and managers (owner decision 1,
  // G-16); the server leaves it out of a florist's answer, and the page shows
  // no cost tile or cost text to them.
  const seesCost = user?.role === "OWNER" || user?.role === "MANAGER";

  const [locationId, setLocationId] = useState("");
  const [items, setItems] = useState<CatalogItem[]>([]);
  const [bouquets, setBouquets] = useState<BouquetBuildSummary[]>([]);
  const [lines, setLines] = useState<LineDraft[]>([]);
  const [notes, setNotes] = useState("");
  // G-141 (OD-215): office sales are usually paid online — Online is on to
  // start; for a florist nothing is (a florist's sale is a counter sale, so
  // they pick — owner, 2026-10-13).
  const startingPayment = user?.role === "FLORIST" ? OFFICE_PAYMENT_NONE : OFFICE_PAYMENT_DEFAULT;
  const [payment, setPayment] = useState<OfficePaymentChoice>(startingPayment);
  // G-141 (owner 2026-10-13): a "Ring up" goes through its order. The order
  // carries how it was paid, so nothing is asked while its goods are paid;
  // while they are owed, the method of the rest is (one, as on Record handover).
  const [ringUpOrder, setRingUpOrder] = useState<CustomerOrder | null>(null);
  const [ringUpMethod, setRingUpMethod] = useState<StaffOrderPaymentMethod | null>(null);
  const [busy, setBusy] = useState(false);
  // G-84: "Is this from box X?" on a desk sale too.
  const boxPrompt = useBoxPickPrompt();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [customBouquetOpen, setCustomBouquetOpen] = useState(false);

  useEffect(() => {
    if (locationId || !locations.length) return;
    const preferred =
      !isOwner && user?.assignedLocation?.id
        ? user.assignedLocation.id
        : locations[0]!.id;
    const valid = locations.some((l) => l.id === preferred);
    setLocationId(valid ? preferred : locations[0]!.id);
  }, [locations, locationId, isOwner, user?.assignedLocation?.id]);

  useEffect(() => {
    if (!linkOrderId) return;
    void api
      .getCustomerOrder(linkOrderId)
      .then((order) => {
        setRingUpOrder(order);
        // The ring-up sells from the order's shop.
        setLocationId(order.location.id);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : "Could not load the order"),
      );
  }, [linkOrderId]);

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === locationId) ?? null,
    [locations, locationId],
  );

  useEffect(() => {
    void api
      .listItems()
      // G-40 (owner, 2026-08-01): single stems sell at the till too — the
      // server dropped its perishable guard on 2026-08-24; this page kept
      // hiding flowers until 2026-09-09.
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  const refreshBouquets = useCallback(async () => {
    if (!locationId) {
      setBouquets([]);
      return;
    }
    try {
      const list = await api.listBouquetsInStock(locationId);
      setBouquets(list);
    } catch {
      setBouquets([]);
    }
  }, [locationId]);

  useEffect(() => {
    void refreshBouquets();
  }, [refreshBouquets]);

  // KI-157: a ring-up starts with the bouquets the order holds already on the
  // ticket — the order's own build and every shelf bouquet set aside for it —
  // at the price agreed for each (else the bouquet's suggestion). The server
  // refuses a ring-up without them, so the florist should never have to guess
  // which "Custom bouquet" is the order's. Once, so a line removed stays removed.
  const heldPrefilled = useRef(false);
  useEffect(() => {
    if (heldPrefilled.current || !ringUpOrder || bouquets.length === 0) return;
    const held = bouquets.filter((b) => b.forOrder?.id === ringUpOrder.id);
    heldPrefilled.current = true;
    if (held.length === 0) return;
    const agreedPrice = (buildId: string): string | null => {
      if (ringUpOrder.bouquetBuild?.id === buildId) return ringUpOrder.bouquetPriceEur;
      return (
        ringUpOrder.lines.find(
          (l) => l.role === "READY_BOUQUET" && l.bouquetBuild?.id === buildId,
        )?.unitPriceEur ?? null
      );
    };
    setLines((ls) => [
      ...held
        .filter((b) => !ls.some((l) => l.kind === "BOUQUET" && l.bouquetBuildId === b.id))
        .map((b) => ({
          uid: newUid(),
          kind: "BOUQUET" as const,
          bouquetBuildId: b.id,
          unitPriceEur: agreedPrice(b.id) ?? b.suggestedSalePriceEur ?? "",
        })),
      ...ls,
    ]);
  }, [ringUpOrder, bouquets]);

  const usedBouquetIds = useMemo(
    () =>
      new Set(
        lines
          .filter((l): l is Extract<LineDraft, { kind: "BOUQUET" }> => l.kind === "BOUQUET")
          .map((l) => l.bouquetBuildId)
          .filter(Boolean),
      ),
    [lines],
  );

  const addBouquetLine = () =>
    setLines((ls) => [
      ...ls,
      { uid: newUid(), kind: "BOUQUET", bouquetBuildId: "", unitPriceEur: "" },
    ]);

  const addItemLine = () =>
    setLines((ls) => [
      ...ls,
      {
        uid: newUid(),
        kind: "ITEM",
        itemId: "",
        quantity: "1",
        unitPriceEur: "",
      },
    ]);

  const handleCustomBouquetBuilt = useCallback(
    (built: BouquetBuildSummary) => {
      setBouquets((prev) =>
        prev.some((b) => b.id === built.id) ? prev : [built, ...prev],
      );
      setLines((ls) => [
        ...ls,
        {
          uid: newUid(),
          kind: "BOUQUET",
          bouquetBuildId: built.id,
          unitPriceEur: built.suggestedSalePriceEur ?? "",
        },
      ]);
      setCustomBouquetOpen(false);
      const suggested = `suggested price ${built.suggestedSalePriceEur != null ? `€${Number(built.suggestedSalePriceEur).toFixed(2)}` : "—"}`;
      setSuccess(
        `Custom bouquet built (${seesCost ? `${afterCostLabel("cost to build", bouquetCostText(built, fmt))}, ` : ""}${suggested}). Adjust the sale price if needed, then submit the ticket.`,
      );
      void refreshBouquets();
    },
    [refreshBouquets, seesCost],
  );

  const removeLine = (uid: string) =>
    setLines((ls) => ls.filter((l) => l.uid !== uid));

  const updateLine = (uid: string, patch: Partial<LineDraft>) =>
    setLines((ls) =>
      ls.map((l) => (l.uid === uid ? ({ ...l, ...patch } as LineDraft) : l)),
    );

  const totals = useMemo(() => {
    let revenue = 0;
    let estCogs = 0;
    // WO-B (OD-198): a bouquet with a component of unknown cost adds only its
    // known part, so the preview's cost is then partly unknown and its gross "—".
    let costIncomplete = false;
    for (const l of lines) {
      const price = Number(l.unitPriceEur);
      if (!Number.isFinite(price)) continue;
      if (l.kind === "BOUQUET") {
        revenue += price;
        const b = bouquets.find((x) => x.id === l.bouquetBuildId);
        if (b) {
          const known = knownAmount(b.totalCogsEur);
          estCogs += known ?? 0;
          if (known == null || hasUnknown(b.unknownCostComponentCount)) costIncomplete = true;
        }
      } else {
        const qty = Number(l.quantity);
        if (Number.isFinite(qty)) {
          revenue += qty * price;
        }
      }
    }
    return { revenue, estCogs, gross: revenue - estCogs, costIncomplete };
  }, [lines, bouquets]);

  // The total the payment must add up to, each line rounded as the server does.
  const totalCents = useMemo(
    () =>
      lines.reduce(
        (cents, l) => cents + lineCents(Number(l.unitPriceEur), l.kind === "BOUQUET" ? 1 : Number(l.quantity) || 0),
        0,
      ),
    [lines],
  );
  const paymentReading = readOfficePaymentChoice(payment, totalCents);
  // G-141 (option A, KI-129): the ring-up's sale is the price charged, so
  // the order owes the ticket plus its courier fee, less what was paid — the
  // handover's rule; the server takes exactly this and refuses without a method.
  const ringUpPaidCents = ringUpOrder ? Math.round(Number(ringUpOrder.paidEur) * 100) : 0;
  const ringUpOwedCents = ringUpOrder
    ? totalCents + Math.round(Number(ringUpOrder.deliveryFeeEur) * 100)
    : 0;
  const ringUpDueCents = Math.max(0, ringUpOwedCents - ringUpPaidCents);
  const ringUpOwed = ringUpOrder != null && ringUpDueCents > 0;
  const paymentComplete = linkOrderId
    ? ringUpOrder != null && (!ringUpOwed || ringUpMethod != null)
    : paymentReading.ok;

  const submit = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      if (!locationId) throw new Error("Select a shop");
      if (lines.length === 0) throw new Error("Add at least one line");

      const payloadLines: CreateSaleLinePayload[] = lines.map((l) => {
        if (l.kind === "BOUQUET") {
          if (!l.bouquetBuildId) throw new Error("Pick a bouquet for every bouquet line");
          const price = Number(l.unitPriceEur);
          if (l.unitPriceEur.trim() === "" || !Number.isFinite(price) || price < 0) {
            throw new Error("Bouquet price must be a number");
          }
          return {
            kind: "BOUQUET",
            bouquetBuildId: l.bouquetBuildId,
            quantity: 1,
            unitPriceEur: price,
          };
        }
        if (!l.itemId) throw new Error("Pick an item for every item line");
        const qty = Number(l.quantity);
        if (!Number.isFinite(qty) || qty <= 0) {
          throw new Error("Item quantity must be > 0");
        }
        const price = Number(l.unitPriceEur);
        if (l.unitPriceEur.trim() === "" || !Number.isFinite(price) || price < 0) {
          throw new Error("Item price must be a number");
        }
        return {
          kind: "ITEM",
          itemId: l.itemId,
          quantity: qty,
          unitPriceEur: price,
        };
      });

      const notesPart = notes.trim() ? { notes: notes.trim() } : {};
      let sale: Sale;
      let orderNote = "";
      if (linkOrderId) {
        // G-141: the sale and the order's completion are saved together —
        // a refusal leaves no sale behind.
        if (!ringUpOrder) throw new Error("The order is still loading");
        if (ringUpOwed && !ringUpMethod) throw new Error("Pick how it was paid.");
        const body: RingUpOrderPayload = {
          lines: payloadLines,
          ...(ringUpOwed && ringUpMethod ? { method: ringUpMethod } : {}),
          ...notesPart,
        };
        const rungUp = await boxPrompt.run((boxAnswers) =>
          api.ringUpCustomerOrder(linkOrderId, boxAnswers.length ? { ...body, boxAnswers } : body),
        );
        sale = rungUp.sale;
        orderNote = ` Order ${rungUp.order.code} completed.`;
      } else {
        if (!paymentReading.ok) {
          throw new Error(officePaymentProblemText(paymentReading.problem, totalCents, fmt));
        }
        const body: CreateSalePayload = {
          locationId,
          lines: payloadLines,
          payments: paymentReading.parts,
          ...notesPart,
        };
        sale = await boxPrompt.run((boxAnswers) =>
          api.createSale(boxAnswers.length ? { ...body, boxAnswers } : body),
        );
      }
      setSuccess(
        `Sale recorded: ${fmt(Number(sale.totalEur))} (${sale.lines.length} line${sale.lines.length === 1 ? "" : "s"}).${orderNote}`,
      );
      setLines([]);
      setNotes("");
      setPayment(startingPayment);
      await refreshBouquets();
      if (user?.role !== "FLORIST" && !embedOnHome) {
        setTimeout(() => navigate("/app/sales"), 800);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not record sale");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
      {boxPrompt.element}
      {embedOnHome ? (
        <div>
          <h3 className="page-title" style={{ margin: 0, fontSize: "1.15rem" }}>
            Add a sale
          </h3>
          <p className="page-subtitle" style={{ margin: "var(--space-2) 0 0" }}>
            Bouquets in stock, custom build, or non-flower lines — same as the full page.
          </p>
        </div>
      ) : (
        <div>
          <h2 className="page-title">Add a sale</h2>
          <p className="page-subtitle">
            Attach a bouquet that&apos;s already in stock, build a custom bouquet from flowers on
            hand, or add any non-flower item. Multiple lines per ticket are fine.
          </p>
        </div>
      )}

      {error ? <div className="alert error">{error}</div> : null}
      {success ? <div className="alert success">{success}</div> : null}

      <div className="card">
        <div className="form-grid">
          <div className="field">
            <label htmlFor="sale-location">Shop *</label>
            <select
              id="sale-location"
              value={locationId}
              onChange={(e) => {
                setLocationId(e.target.value);
                setLines([]);
              }}
              disabled={Boolean(linkOrderId) || (!isOwner && locations.length === 1)}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name} ({l.code})
                </option>
              ))}
            </select>
            {!isOwner ? (
              <small className="hint">
                Sales can only be recorded for your currently assigned shop.
              </small>
            ) : null}
          </div>
          <div className="field">
            <label htmlFor="sale-notes">Notes (optional)</label>
            <input
              id="sale-notes"
              type="text"
              value={notes}
              maxLength={500}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. wedding gift"
            />
          </div>
        </div>
      </div>

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Lines</h3>
          <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
            <button
              type="button"
              className="button tinted"
              onClick={addBouquetLine}
              disabled={!locationId}
            >
              + Bouquet in stock
            </button>
            <button
              type="button"
              className="button tinted"
              onClick={() => setCustomBouquetOpen(true)}
              disabled={!selectedLocation || selectedLocation.zones.length === 0}
              title={
                selectedLocation && selectedLocation.zones.length === 0
                  ? "This shop has no zones yet"
                  : undefined
              }
            >
              + Custom bouquet
            </button>
            <button
              type="button"
              className="button tinted"
              onClick={addItemLine}
              disabled={!locationId}
            >
              + Item
            </button>
          </div>
        </div>

        {lines.length === 0 ? (
          <div className="empty-state" style={{ padding: "var(--space-4)" }}>
            No lines yet. Attach an existing bouquet, build a custom one from in-stock flowers, or
            add a non-flower item.
          </div>
        ) : (
          lines.map((line, idx) => (
            <SaleLineRow
              key={line.uid}
              line={line}
              index={idx}
              bouquets={bouquets}
              items={items}
              usedBouquetIds={usedBouquetIds}
              ringUpOrderId={linkOrderId}
              seesCost={seesCost}
              onChange={(patch) => updateLine(line.uid, patch)}
              onRemove={() => removeLine(line.uid)}
            />
          ))
        )}
      </div>

      <div className="card">
        {linkOrderId ? (
          <div className="office-payment">
            <h3 className="office-payment-title">How was it paid?</h3>
            {ringUpOrder == null ? (
              <p className="muted office-payment-owed">…</p>
            ) : (
              <p className="office-payment-owed">
                Paid on {ringUpOrder.code} <strong>{fmt(ringUpPaidCents / 100)}</strong>
              </p>
            )}
            {ringUpOwed ? (
              <>
                <div className="office-payment-buttons" role="group" aria-label="How was it paid?">
                  {ORDER_PAYMENT_METHODS.map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      aria-pressed={ringUpMethod === m.value}
                      className={ringUpMethod === m.value ? "is-active" : ""}
                      onClick={() => setRingUpMethod(m.value)}
                      disabled={busy}
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <p className="office-payment-owed">
                  Still to pay <strong>{fmt(ringUpDueCents / 100)}</strong>
                </p>
              </>
            ) : ringUpOrder != null && lines.length > 0 && ringUpPaidCents > ringUpOwedCents ? (
              <p className="office-payment-owed">
                Overpaid <strong>{fmt((ringUpPaidCents - ringUpOwedCents) / 100)}</strong>
              </p>
            ) : null}
          </div>
        ) : (
          <OfficePaymentPicker
            value={payment}
            onChange={setPayment}
            totalCents={totalCents}
            money={fmt}
            disabled={busy}
          />
        )}
      </div>

      <div className="card" style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <div style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap" }}>
          <Stat label="Revenue" value={fmt(totals.revenue)} />
          {/* Bouquets only: an item line's cost of goods is worked out on the
              server at submit (oldest lot first), so the preview cannot show it.
              G-170 (KI-152): a florist sees no cost figures. */}
          {seesCost ? (
            <>
              <Stat
                label="Cost of goods (est., bouquets only)"
                value={partlyKnownCost(totals.estCogs, totals.costIncomplete, fmt)}
              />
              <Stat
                label="Margin with IVA (est.)"
                value={totals.costIncomplete ? NO_FIGURE : fmt(totals.gross)}
              />
            </>
          ) : null}
        </div>
        <div className="button-row">
          <button
            type="button"
            className="button"
            disabled={busy || !locationId || lines.length === 0 || !paymentComplete}
            onClick={() => void submit()}
          >
            {busy ? "Recording…" : "Record sale"}
          </button>
        </div>
      </div>

      {customBouquetOpen && selectedLocation ? (
        <BuildBouquetForSaleModal
          location={selectedLocation}
          onClose={() => setCustomBouquetOpen(false)}
          onBuilt={handleCustomBouquetBuilt}
        />
      ) : null}
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12, textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

function SaleLineRow({
  line,
  index,
  bouquets,
  items,
  usedBouquetIds,
  ringUpOrderId,
  seesCost,
  onChange,
  onRemove,
}: {
  line: LineDraft;
  index: number;
  bouquets: BouquetBuildSummary[];
  items: CatalogItem[];
  usedBouquetIds: Set<string>;
  ringUpOrderId: string | null;
  /** KI-152: whether the viewer may see a bouquet's cost (owner, manager). */
  seesCost: boolean;
  onChange: (patch: Partial<LineDraft>) => void;
  onRemove: () => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr auto",
        gap: "var(--space-3)",
        alignItems: "flex-end",
        padding: "var(--space-3)",
        borderRadius: "var(--radius-md)",
        background: "var(--color-surface-muted)",
      }}
    >
      <div className="muted" style={{ fontWeight: 600, fontSize: 13, paddingBottom: 12 }}>
        #{index + 1}
      </div>

      {line.kind === "BOUQUET" ? (
        <BouquetLineFields
          line={line}
          bouquets={bouquets}
          usedBouquetIds={usedBouquetIds}
          ringUpOrderId={ringUpOrderId}
          seesCost={seesCost}
          onChange={onChange}
        />
      ) : (
        <ItemLineFields line={line} items={items} onChange={onChange} />
      )}

      <button type="button" className="button plain" onClick={onRemove} aria-label="Remove line">
        Remove
      </button>
    </div>
  );
}

function BouquetLineFields({
  line,
  bouquets,
  usedBouquetIds,
  ringUpOrderId,
  seesCost,
  onChange,
}: {
  line: Extract<LineDraft, { kind: "BOUQUET" }>;
  bouquets: BouquetBuildSummary[];
  usedBouquetIds: Set<string>;
  /** G-141: the order being rung up — the bouquets it holds are its to sell. */
  ringUpOrderId: string | null;
  /** KI-152: whether the viewer may see a bouquet's cost (owner, manager). */
  seesCost: boolean;
  onChange: (patch: Partial<LineDraft>) => void;
}) {
  const selected = bouquets.find((b) => b.id === line.bouquetBuildId);
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 160px",
        gap: "var(--space-3)",
        alignItems: "flex-end",
      }}
    >
      <div className="field">
        <label>Bouquet (in stock)</label>
        <select
          value={line.bouquetBuildId}
          onChange={(e) => {
            const buildId = e.target.value;
            const build = bouquets.find((b) => b.id === buildId);
            onChange({
              bouquetBuildId: buildId,
              unitPriceEur: build?.suggestedSalePriceEur ?? "",
            });
          }}
        >
          <option value="">— Select bouquet —</option>
          {bouquets.map((b) => {
            // OD-31: an order's bouquet is never walk-in stock — the server
            // refuses it, so the picker says why instead of letting the
            // whole multi-line sale 400 at submit. `forOrder` is the
            // server's own reading of the hold (G-112), cancellations freed.
            const spokenFor =
              (b.forOrder != null && b.forOrder.id !== ringUpOrderId) ||
              (b.shopifyOrderLine != null &&
                b.shopifyOrderLine.order.status !== "CANCELLED");
            return (
              <option
                key={b.id}
                value={b.id}
                disabled={
                  spokenFor ||
                  (line.bouquetBuildId !== b.id && usedBouquetIds.has(b.id))
                }
              >
                {b.template?.name ?? "Custom bouquet"} · {seesCost ? `${afterCostLabel("cost", bouquetCostText(b, fmt))} · ` : ""}suggested{" "}
                {b.suggestedSalePriceEur != null
                  ? `€${Number(b.suggestedSalePriceEur).toFixed(2)}`
                  : "—"} ·{" "}
                {new Date(b.createdAt).toLocaleDateString()}
                {/* KI-157: the order being rung up names its own bouquet too. */}
                {b.forOrder
                  ? ` · for ${b.forOrder.code}`
                  : spokenFor
                    ? " · for a Shopify order"
                    : ""}
              </option>
            );
          })}
        </select>
        {selected ? (
          <small className="hint">
            Built by {selected.builtBy.fullName} ·{" "}
            {selected.lines.map((l) => `${Number(l.quantity)}× ${l.item.name}`).join(", ")} ·
            suggested {selected.suggestedSalePriceEur != null
              ? `€${Number(selected.suggestedSalePriceEur).toFixed(2)}`
              : "—"}
          </small>
        ) : (
          <small className="hint">
            Lists every bouquet currently in stock at this shop. Use{" "}
            <strong>+ Custom bouquet</strong> if you need to make a fresh one for the customer.
          </small>
        )}
      </div>
      <div className="field">
        <label>Sale price (€)</label>
        <input
          type="number"
          min={0}
          step="0.01"
          value={line.unitPriceEur}
          onChange={(e) => onChange({ unitPriceEur: e.target.value })}
        />
      </div>
    </div>
  );
}

function ItemLineFields({
  line,
  items,
  onChange,
}: {
  line: Extract<LineDraft, { kind: "ITEM" }>;
  items: CatalogItem[];
  onChange: (patch: Partial<LineDraft>) => void;
}) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 100px 160px",
        gap: "var(--space-3)",
        alignItems: "flex-end",
      }}
    >
      <div className="field">
        <label>Item</label>
        <select
          value={line.itemId}
          onChange={(e) => {
            const itemId = e.target.value;
            const item = items.find((i) => i.id === itemId);
            onChange({
              itemId,
              unitPriceEur: item?.defaultSaleUnitPriceEur ?? "",
            });
          }}
        >
          <option value="">— Select item —</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name} ({i.category.label})
              {i.defaultSaleUnitPriceEur != null
                ? ` · €${Number(i.defaultSaleUnitPriceEur).toFixed(2)}`
                : " · price missing"}
            </option>
          ))}
        </select>
        <small className="hint">
          Single stems can be sold here too — the price is per stem, and the
          floor is judged against the batch the stems come from.
        </small>
      </div>
      <div className="field">
        <label>Qty</label>
        <input
          type="number"
          min={0.001}
          step="0.001"
          value={line.quantity}
          onChange={(e) => onChange({ quantity: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Unit price (€)</label>
        <input
          type="number"
          min={0}
          step="0.01"
          value={line.unitPriceEur}
          onChange={(e) => onChange({ unitPriceEur: e.target.value })}
        />
      </div>
    </div>
  );
}
